import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui';
import { Badge } from '@/components/ui';
import { KeywordsTable } from './KeywordsTable';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { getInternationalData } from '@/data/mocks/international';
import { Globe, Search, TrendingUp, Languages } from 'lucide-react';

interface InternationalSectionProps {
  restaurantId: string;
}

export const InternationalSection: React.FC<InternationalSectionProps> = ({ restaurantId }) => {
  const data = getInternationalData(restaurantId);

  // Préparation des données pour les graphiques
  const trafficData = Object.values(data.traffic).map(item => ({
    name: item.language.toUpperCase(),
    traffic: item.traffic,
    percentage: item.percentage
  }));

  const intentData = data.searchIntents.map(intent => ({
    name: intent.type,
    value: intent.percentage
  }));

  const COLORS = ['#10B981', '#3B82F6', '#F59E0B'];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">SEO International</h2>
        <Badge variant="outline" className="text-sm">
          {Object.values(data.traffic).reduce((sum, item) => sum + item.traffic, 0).toLocaleString()} 
          visites/mois
        </Badge>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Mots-clés EN/ES/PT</p>
                <p className="text-2xl font-bold">
                  {Object.values(data.traffic)
                    .filter(item => item.language !== 'fr')
                    .reduce((sum, item) => sum + item.keywords, 0)
                    .toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Languages className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Brand vs Non-Brand</p>
                <p className="text-2xl font-bold">
                  {data.brandMetrics.nonBranded.percentage}% Non-Brand
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Search className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-500">Position Moyenne</p>
                <p className="text-2xl font-bold">
                  {(Object.values(data.traffic)
                    .reduce((sum, item) => sum + item.avgPosition, 0) / 
                    Object.values(data.traffic).length
                  ).toFixed(1)}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Trafic par langue */}
        <Card>
          <CardHeader>
            <CardTitle>Trafic par Langue</CardTitle>
            <CardDescription>Distribution du trafic international</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trafficData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="traffic" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Intentions de recherche */}
        <Card>
          <CardHeader>
            <CardTitle>Intentions de Recherche</CardTitle>
            <CardDescription>Distribution des types de requêtes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={intentData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {intentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 mt-4">
                {intentData.map((item, index) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS[index] }}
                    />
                    <span className="text-sm">{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Pages Internationales */}
      <Card>
        <CardHeader>
          <CardTitle>Top Pages Internationales</CardTitle>
          <CardDescription>Pages les plus performantes par langue</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">URL</th>
                  <th className="text-left py-3 px-4">Langue</th>
                  <th className="text-right py-3 px-4">Trafic</th>
                  <th className="text-right py-3 px-4">Mots-clés</th>
                  <th className="text-right py-3 px-4">Conv. Rate</th>
                </tr>
              </thead>
              <tbody>
                {data.topPages.map((page) => (
                  <tr key={page.url} className="border-b">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium">{page.url}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="outline">{page.language.toUpperCase()}</Badge>
                    </td>
                    <td className="text-right py-3 px-4">
                      {page.traffic.toLocaleString()}
                    </td>
                    <td className="text-right py-3 px-4">{page.keywords}</td>
                    <td className="text-right py-3 px-4">
                      <Badge variant={page.conversionRate > 7 ? 'success' : 'default'}>
                        {page.conversionRate.toFixed(1)}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Tableaux de mots-clés par langue */}
      {['en', 'es', 'pt'].map((lang) => (
        <Card key={lang}>
          <CardHeader>
            <CardTitle>Top Mots-clés {lang.toUpperCase()}</CardTitle>
            <CardDescription>
              {data.topKeywords[lang].length} mots-clés analysés
            </CardDescription>
          </CardHeader>
          <CardContent>
            <KeywordsTable
              keywords={data.topKeywords[lang]}
              language={lang as 'en' | 'es' | 'pt'}
            />
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
